import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { dt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Store, CheckCircle2, AlertCircle, Link2, EyeOff, RefreshCw, ScrollText, ShoppingCart, Eye, Bell } from "lucide-react";
import { toast } from "sonner";

// Fix T (2026-10-23): la web (Shopify) manda en el catálogo. Esta pantalla no publica productos: lee la web, muestra qué
// cambiaría y deja que el admin decida quién lleva el stock y si la actualización es automática. Todo arranca apagado.
const REVISION_HORAS = [1, 3, 6, 12, 24];

const MODOS_STOCK = {
  sin_definir: {
    titulo: "Todavía sin elegir",
    ayuda: "El stock no se mueve en ninguna dirección: ni del sistema a la web ni de la web al sistema.",
  },
  sistema: {
    titulo: "Lo lleva el sistema",
    ayuda: "El stock de cada producto vinculado se envía a Shopify cuando cambia. Lo que se edite a mano en Shopify se vuelve a reemplazar.",
  },
  tienda: {
    titulo: "Lo lleva la tienda (Shopify)",
    ayuda: "El stock se trae de la web al revisar y cuando la web avisa, como un ajuste con origen «Shopify». El sistema no lo envía de vuelta.",
  },
};

const CONFIRMAR_MODO = {
  sistema: "A partir de ahora el stock de cada producto vinculado con la web se envía a Shopify cuando cambia, y reemplaza lo que haya cargado allá. ¿Confirmás que el sistema lleva el stock?",
  tienda: "A partir de ahora las cantidades se traen de la web (al revisar y cuando la web avisa) y reemplazan el stock del sistema como un ajuste con origen «Shopify». ¿Confirmás que la tienda lleva el stock?",
};

const LOG_ACCIONES = {
  foto: "Foto de la web", catalogo_auto: "Cambios aplicados desde la web", aviso_eliminado: "Producto borrado en la web",
  aviso: "Aviso de la web", catalogo_revision: "Revisión de la web", vincular: "Vínculo con la web", ignorar: "Producto ignorado",
  stock: "Stock enviado a la web",
};

const AVISO_TOPICS = {
  "products/create": "Producto nuevo", "products/update": "Producto cambiado", "products/delete": "Producto borrado",
  PRODUCTS_CREATE: "Producto nuevo", PRODUCTS_UPDATE: "Producto cambiado", PRODUCTS_DELETE: "Producto borrado",
};

const AVISO_RESULTADOS = {
  apagado: "solo se anotó (la actualización automática está apagada)", encolado: "quedó en cola para aplicarse",
  eliminado: "el producto se desactivó", sin_id: "sin identificador, se ignoró", error: "no se pudo procesar",
};

const AVISO_REGISTRO = { creado: "registrado", ya_estaba: "ya estaba registrado", error: "no se pudo registrar" };

const irA = (url) => { window.location.assign(url); };

function Paso({ n, titulo, testid, children }) {
  return (
    <div className="ms-card p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center shrink-0">{n}</div>
        <h3 className="font-head font-semibold">{titulo}</h3>
      </div>
      {children}
    </div>
  );
}

export default function ShopifySync() {
  const { user } = useAuth();
  const esAdmin = user?.role === "admin";
  const [estado, setEstado] = useState(null);
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [pendiente, setPendiente] = useState(null);
  const [matching, setMatching] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [vista, setVista] = useState(null);
  const [revision, setRevision] = useState(null);
  const [envio, setEnvio] = useState(null);
  const [avisosRes, setAvisosRes] = useState(null);
  const [registrando, setRegistrando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  const timers = useRef(new Set());

  const cargar = async () => {
    try {
      const [e, l, w, i] = await Promise.all([
        api.get("/shopify/estado"), api.get("/shopify/log"), api.get("/shopify/webhook-logs"),
        api.get("/catalog-import").catch(() => ({ data: [] })),
      ]);
      setEstado(e.data); setLog(l.data); setWebhookLogs(w.data);
      const lista = Array.isArray(i.data) ? i.data : i.data.items || [];
      setPendiente(lista.find((x) => x.origen === "shopify_api" && x.status === "revision") || null);
      setError(null);
      return e.data;
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo cargar la pantalla.");
      return null;
    }
  };

  // Lee el avance de un trabajo cada 1,5 s hasta que termina (o hasta 3 fallos seguidos al consultarlo).
  const seguir = (ruta, id, setJob, alTerminar) => {
    let fallos = 0;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`${ruta}/${id}`);
        fallos = 0;
        setJob(data);
        if (data.estado !== "en_progreso") {
          clearInterval(t); timers.current.delete(t);
          alTerminar(data);
        }
      } catch {
        fallos += 1;
        if (fallos >= 3) {
          clearInterval(t); timers.current.delete(t);
          setJob({ id, estado: "error", error: "No se pudo consultar el avance. Recargá la página para ver cómo terminó." });
        }
      }
    }, 1500);
    timers.current.add(t);
  };

  const alTerminarVista = async (j) => {
    await cargar();
    if (j.estado === "completado") toast.success("Listo: ya podés revisar los productos de la web");
    else toast.error(j.error || "No se pudo leer la web");
  };
  const alTerminarRevision = async (j) => {
    await cargar();
    if (j.estado === "error") toast.error(j.error || "No se pudo revisar la web");
    else if (j.omitido) toast.info(j.omitido);
    else toast.success(`Revisión lista: ${j.resumen?.creados || 0} creados, ${j.resumen?.actualizados || 0} actualizados`);
  };
  const alTerminarEnvio = async (j) => {
    await cargar();
    toast.success(`Envío completo: ${j.procesados}/${j.total} productos · ${j.errores} error(es)`);
  };

  useEffect(() => {
    let vivo = true;
    (async () => {
      const e = await cargar();
      if (!vivo) return;
      const activos = e?.trabajos_activos || {};
      if (activos.vista_previa) { setVista(activos.vista_previa); seguir("/shopify/trabajos", activos.vista_previa.id, setVista, alTerminarVista); }
      if (activos.revision) { setRevision(activos.revision); seguir("/shopify/trabajos", activos.revision.id, setRevision, alTerminarRevision); }
      if (activos.envio_stock) { setEnvio(activos.envio_stock); seguir("/shopify/sincronizar-todo", activos.envio_stock.id, setEnvio, alTerminarEnvio); }
    })();
    const activos = timers.current;
    return () => { vivo = false; activos.forEach(clearInterval); };
    // eslint-disable-next-line
  }, []);

  const verProductos = async () => {
    setVista({ estado: "en_progreso" });
    try {
      const { data } = await api.post("/shopify/vista-previa");
      seguir("/shopify/trabajos", data.job_id, setVista, alTerminarVista);
    } catch (e) { setVista(null); toast.error(e.response?.data?.detail || "No se pudo empezar"); }
  };

  const revisarAhora = async () => {
    setRevision({ estado: "en_progreso" });
    try {
      const { data } = await api.post("/shopify/revisar-ahora");
      seguir("/shopify/trabajos", data.job_id, setRevision, alTerminarRevision);
    } catch (e) { setRevision(null); toast.error(e.response?.data?.detail || "No se pudo empezar"); }
  };

  const enviarStock = async () => {
    setEnvio({ estado: "en_progreso", total: 0, procesados: 0, errores: 0 });
    try {
      const { data } = await api.post("/shopify/sincronizar-todo");
      seguir("/shopify/sincronizar-todo", data.job_id, setEnvio, alTerminarEnvio);
    } catch (e) { setEnvio(null); toast.error(e.response?.data?.detail || "No se pudo empezar"); }
  };

  const guardarConfig = async (cambios, mensaje) => {
    setGuardando(true);
    try {
      const { data } = await api.put("/shopify/config", cambios);
      setEstado((anterior) => ({ ...anterior, config: data.config }));
      toast.success(mensaje || "Guardado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setGuardando(false); await cargar(); }
  };

  const elegirModo = (modo) => {
    if (modo === estado.config.stock_modo) return;
    if (modo === "sin_definir") { guardarConfig({ stock_modo: modo }, "El stock no se mueve en ninguna dirección"); return; }
    setConfirmar({
      titulo: modo === "sistema" ? "El sistema lleva el stock" : "La tienda lleva el stock", texto: CONFIRMAR_MODO[modo],
      boton: "Sí, confirmar", accion: () => guardarConfig({ stock_modo: modo }, "Listo: " + MODOS_STOCK[modo].titulo.toLowerCase()),
    });
  };

  const cambiarAutomatico = (encender) => {
    if (!encender) { guardarConfig({ catalogo_auto: false }, "Actualización automática apagada"); return; }
    setConfirmar({
      titulo: "Encender la actualización automática",
      texto: "Los cambios de la web (nombre, precio, descripción, fotos y si el producto está activo) se van a aplicar solos, cuando la web avisa y en cada revisión. Lo que coincide solo por nombre o repite SKU no se aplica solo. El stock sigue la opción que elegiste.",
      boton: "Sí, encender", accion: () => guardarConfig({ catalogo_auto: true }, "Actualización automática encendida"),
    });
  };

  const pedirEnvio = () => setConfirmar({
    titulo: "Enviar todo el stock a la web",
    texto: `Se va a enviar a Shopify el stock del sistema de los ${estado.productos.vinculados} productos vinculados con la web. Lo que tenga la tienda cargado se reemplaza.`,
    boton: "Sí, enviar", accion: enviarStock,
  });

  const registrarAvisos = async () => {
    setRegistrando(true);
    try {
      const { data } = await api.post("/shopify/avisos/registrar");
      setAvisosRes(data.avisos);
      if (data.avisos.some((a) => a.estado === "error")) toast.error("Algún aviso no se pudo registrar. Mirá el detalle.");
      else toast.success("Avisos registrados en la tienda");
      await cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudieron registrar los avisos"); }
    finally { setRegistrando(false); }
  };

  const buscarCoincidencias = async () => {
    setBuscando(true);
    try { const { data } = await api.get("/shopify/matching"); setMatching(data); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo leer la web"); }
    finally { setBuscando(false); }
  };

  const resolver = async (item, accion) => {
    try {
      const cuerpo = { product_id: item.product_id, accion };
      if (accion === "vincular") {
        cuerpo.shopify_product_id = String(item.shopify_product_id);
        cuerpo.shopify_variant_id = String(item.shopify_variant_id);
      }
      await api.post("/shopify/matching/resolver", cuerpo);
      toast.success(accion === "vincular" ? "Vinculado" : "Ignorado");
      setMatching((lista) => (lista || []).filter((m) => m.product_id !== item.product_id));
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (error && !estado) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <EmptyState icon={AlertCircle} title="No se pudo cargar Shopify" subtitle={error} />
        <div className="text-center mt-3"><Button variant="outline" className="rounded-full" onClick={cargar}>Reintentar</Button></div>
      </div>
    );
  }
  if (!estado) return <div className="p-8"><Loader /></div>;

  const cfg = estado.config;
  const conectado = estado.credenciales;
  const horas = [...new Set([...REVISION_HORAS, cfg.revision_horas])].sort((a, b) => a - b);
  const modo = MODOS_STOCK[cfg.stock_modo] || MODOS_STOCK.sin_definir;
  const inicial = estado.importacion_inicial;
  const ultimo = cfg.ultimo_resultado;
  const corriendoVista = vista?.estado === "en_progreso";
  const corriendoRevision = revision?.estado === "en_progreso" || cfg.revision_en_curso;
  const corriendoEnvio = envio?.estado === "en_progreso";
  const porRevisar = vista?.estado === "completado" ? vista.importacion_id : pendiente?.id;
  const resumen = vista?.estado === "completado" ? vista.resumen : null;

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Sincronización Shopify" subtitle="La web manda en el catálogo: productos, precios, fotos y si están activos se leen de Shopify. El sistema no crea ni cambia productos en la tienda." />
      {!esAdmin && <p className="text-xs text-muted-foreground mb-3" data-testid="shopify-solo-lectura">Podés ver el estado y leer los productos de la web. Los cambios de configuración los hace el admin.</p>}
      <Tabs defaultValue="estado">
        <TabsList className="rounded-full mb-4 flex-wrap h-auto">
          <TabsTrigger value="estado" className="rounded-full gap-1" data-testid="tab-shopify-estado"><Store className="h-4 w-4" /> Estado</TabsTrigger>
          <TabsTrigger value="matching" className="rounded-full gap-1" data-testid="tab-shopify-matching"><Link2 className="h-4 w-4" /> Vinculación</TabsTrigger>
          <TabsTrigger value="log" className="rounded-full gap-1" data-testid="tab-shopify-log"><ScrollText className="h-4 w-4" /> Registro</TabsTrigger>
        </TabsList>

        <TabsContent value="estado">
          <div className="space-y-4">
            <div className="ms-card p-5" data-testid="shopify-status-card">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-emerald-600 flex items-center justify-center shrink-0">
                  <Store className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="font-head font-semibold">1. Conexión con Shopify</p>
                  {conectado ? (
                    <Badge className="rounded-full border-0 bg-emerald-50 text-emerald-600 gap-1 mt-1" data-testid="shopify-connected"><CheckCircle2 className="h-3 w-3" /> Conectado ({estado.shop_domain})</Badge>
                  ) : (
                    <Badge className="rounded-full border-0 bg-amber-50 text-amber-600 gap-1 mt-1" data-testid="shopify-pending"><AlertCircle className="h-3 w-3" /> Pendiente de conexión</Badge>
                  )}
                </div>
              </div>
              {conectado && estado.modo_credenciales && (
                <p className="text-xs text-muted-foreground mt-3">
                  {estado.modo_credenciales === "cliente" ? "Acceso con la clave de la app: el sistema pide y renueva el token solo." : "Acceso con un token fijo de la app."}
                </p>
              )}
              {estado.campos_faltantes.length > 0 && (
                <p className="text-xs text-muted-foreground mt-3" data-testid="shopify-faltantes">
                  Faltan variables de entorno: <span className="font-mono">{estado.campos_faltantes.join(", ")}</span>.
                  {conectado ? " Sin ellas los avisos y pedidos de la web no se pueden validar." : " Ver README_SHOPIFY.md para crear la app de Shopify."}
                </p>
              )}
            </div>

            <Paso n={2} titulo="Productos de la web" testid="shopify-web-card">
              <p className="text-sm text-muted-foreground">
                «Ver productos de la web» lee la tienda y arma una lista para revisar y confirmar en «Importar catálogo». No cambia nada hasta que la confirmes.
              </p>
              <p className="text-xs text-muted-foreground mt-2" data-testid="shopify-productos">
                {estado.productos.total} productos en el sistema · {estado.productos.vinculados} vinculados con la web · {estado.productos.sin_vincular} sin vincular
                {estado.colas.fotos > 0 && ` · ${estado.colas.fotos} fotos por bajar`}
              </p>
              {inicial.hecha ? (
                <p className="text-xs text-emerald-600 mt-2" data-testid="shopify-inicial-hecha">
                  Importación inicial confirmada el {dt(inicial.fecha)}{inicial.resultado ? `: ${inicial.resultado.creados} creados, ${inicial.resultado.actualizados} actualizados` : ""}.
                </p>
              ) : (
                <p className="text-xs text-amber-600 mt-2" data-testid="shopify-inicial-pendiente">
                  Todavía no hay una importación inicial confirmada desde la tienda (la que se hace con un archivo no cuenta para encender la actualización automática).
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button disabled={!conectado || corriendoVista} onClick={verProductos} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="shopify-ver-productos">
                  <Eye className="h-4 w-4" /> {corriendoVista ? "Leyendo la web…" : "Ver productos de la web"}
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => irA("/importar-catalogo")} data-testid="shopify-ir-importar">Importar desde un archivo de Shopify</Button>
              </div>
              {corriendoVista && (
                <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2" data-testid="shopify-preview-running">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Leyendo los productos de la web. Puede tardar unos minutos; si salís de la pantalla, sigue y el resultado queda guardado.
                </p>
              )}
              {vista?.estado === "error" && <p className="text-xs text-red-500 mt-3" data-testid="shopify-preview-error">{vista.error}</p>}
              {!corriendoVista && porRevisar && (
                <div className="rounded-2xl border border-border p-3 mt-3" data-testid="shopify-preview-result">
                  {resumen ? (
                    <>
                      <p className="text-sm font-semibold">Se leyeron {vista.productos_web} productos ({vista.variantes_web} variantes)</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge className="rounded-full border-0 bg-emerald-50 text-emerald-600">{resumen.crear} a crear</Badge>
                        <Badge className="rounded-full border-0 bg-blue-50 text-blue-600">{resumen.actualizar} a actualizar</Badge>
                        <Badge className="rounded-full border-0 bg-gray-100 text-gray-500">{resumen.sin_cambios} sin cambios</Badge>
                        <Badge className="rounded-full border-0 bg-gray-100 text-gray-500">{resumen.omitir} a omitir</Badge>
                      </div>
                      {(resumen.revisar_por_nombre > 0 || resumen.sin_precio > 0 || resumen.sin_sku > 0) && (
                        <p className="text-xs text-amber-600 mt-2">
                          Para mirar con atención: {resumen.revisar_por_nombre} coinciden solo por nombre, {resumen.sin_precio} sin precio, {resumen.sin_sku} sin SKU.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm font-semibold">Hay una lista de la web sin confirmar{pendiente?.created_at ? ` (${dt(pendiente.created_at)})` : ""}.</p>
                  )}
                  <Button onClick={() => irA(`/importar-catalogo?importacion=${porRevisar}`)} className="rounded-full bg-primary hover:bg-primary/90 gap-2 mt-3" data-testid="shopify-revisar-confirmar">
                    Revisar y confirmar
                  </Button>
                </div>
              )}
            </Paso>

            <Paso n={3} titulo="¿Quién lleva el stock?" testid="shopify-stock-card">
              <p className="text-sm text-muted-foreground">Hasta que lo elijas, el stock no se mueve en ninguna dirección. Los pedidos de la web siguen descontando stock en el sistema como siempre.</p>
              <div className="mt-3 max-w-sm">
                <Select value={cfg.stock_modo} onValueChange={elegirModo} disabled={!esAdmin || guardando}>
                  <SelectTrigger className="rounded-xl" data-testid="shopify-stock-modo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MODOS_STOCK).map(([k, v]) => <SelectItem key={k} value={k}>{v.titulo}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground mt-2" data-testid="shopify-stock-ayuda">{modo.ayuda}</p>
              {cfg.stock_modo === "sistema" && (
                <div className="mt-3">
                  {estado.colas.stock > 0 && <p className="text-xs text-muted-foreground mb-2">{estado.colas.stock} productos esperan para enviarse a la web.</p>}
                  <Button disabled={!conectado || !esAdmin || corriendoEnvio} onClick={pedirEnvio} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="shopify-sync-all">
                    <RefreshCw className={`h-4 w-4 ${corriendoEnvio ? "animate-spin" : ""}`} /> {corriendoEnvio ? "Enviando…" : "Enviar todo el stock a la web"}
                  </Button>
                  {envio && (
                    <div className="mt-3" data-testid="shopify-sync-progress">
                      <Progress value={envio.total ? (envio.procesados / envio.total) * 100 : 0} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">{envio.procesados}/{envio.total} productos · {envio.errores} error(es)</p>
                    </div>
                  )}
                </div>
              )}
            </Paso>

            <Paso n={4} titulo="Actualización automática" testid="shopify-auto-card">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Apagada por defecto. Encendida, la web manda: nombre, precio, descripción, fotos y si el producto está activo se actualizan solos.
                  Lo que coincide solo por nombre o repite SKU no se aplica solo.
                </p>
                <Switch checked={!!cfg.catalogo_auto} disabled={!esAdmin || guardando || (!cfg.catalogo_auto && (!conectado || !inicial.hecha))}
                  onCheckedChange={cambiarAutomatico} data-testid="shopify-auto-switch" />
              </div>
              {!cfg.catalogo_auto && (!conectado || !inicial.hecha) && (
                <p className="text-xs text-amber-600 mt-2" data-testid="shopify-auto-requisitos">
                  {!conectado ? "Primero hace falta conectar Shopify." : "Primero confirmá la importación inicial con «Ver productos de la web» (paso 2)."}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <div className="flex items-center gap-2 text-sm">
                  <span>Revisar la web cada</span>
                  <Select value={String(cfg.revision_horas)} onValueChange={(v) => guardarConfig({ revision_horas: Number(v) }, "Guardado")} disabled={!esAdmin || guardando}>
                    <SelectTrigger className="rounded-xl w-24 h-8" data-testid="shopify-horas"><SelectValue /></SelectTrigger>
                    <SelectContent>{horas.map((h) => <SelectItem key={h} value={String(h)}>{h} h</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button variant="outline" disabled={!esAdmin || !conectado || !cfg.catalogo_auto || corriendoRevision} onClick={revisarAhora} className="rounded-full gap-2" data-testid="shopify-revisar-ahora">
                  <RefreshCw className={`h-4 w-4 ${corriendoRevision ? "animate-spin" : ""}`} /> {corriendoRevision ? "Revisando…" : "Revisar ahora"}
                </Button>
              </div>
              {revision?.estado === "error" && <p className="text-xs text-red-500 mt-2">{revision.error}</p>}
              {ultimo && (
                <p className={`text-xs mt-2 ${ultimo.ok ? "text-muted-foreground" : "text-red-500"}`} data-testid="shopify-ultima-revision">
                  {ultimo.ok
                    ? `Última revisión: ${dt(ultimo.fecha)} · ${ultimo.creados || 0} creados, ${ultimo.actualizados || 0} actualizados`
                    : `La última revisión no se pudo completar (${dt(ultimo.fecha)}): ${ultimo.error}`}
                  {ultimo.ok && ultimo.revisar_por_nombre > 0 && ` · ${ultimo.revisar_por_nombre} coinciden solo por nombre: revisalos en «Ver productos de la web»`}
                </p>
              )}
            </Paso>

            <Paso n={5} titulo="Avisos de la web" testid="shopify-avisos-card">
              <p className="text-sm text-muted-foreground">
                La tienda avisa cuando se crea, cambia o borra un producto. Con la actualización automática apagada, los avisos solo se anotan.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {cfg.avisos_registrados ? (
                  <Badge className="rounded-full border-0 bg-emerald-50 text-emerald-600 gap-1" data-testid="shopify-avisos-ok"><Bell className="h-3 w-3" /> Registrados en la tienda</Badge>
                ) : (
                  <Badge className="rounded-full border-0 bg-gray-100 text-gray-500 gap-1" data-testid="shopify-avisos-pendientes"><Bell className="h-3 w-3" /> Sin registrar</Badge>
                )}
                <span className="text-xs text-muted-foreground" data-testid="shopify-avisos-recibidos">
                  {estado.avisos.recibidos} avisos recibidos
                  {estado.avisos.ultimo && ` · último: ${AVISO_TOPICS[estado.avisos.ultimo.topic] || estado.avisos.ultimo.topic}, ${AVISO_RESULTADOS[estado.avisos.ultimo.resultado] || estado.avisos.ultimo.resultado} (${dt(estado.avisos.ultimo.fecha)})`}
                  {estado.colas.avisos > 0 && ` · ${estado.colas.avisos} esperan para aplicarse`}
                </span>
              </div>
              {estado.url_avisos ? (
                <p className="text-xs text-muted-foreground mt-2">Dirección que recibe los avisos: <span className="font-mono break-all">{estado.url_avisos}</span></p>
              ) : (
                <p className="text-xs text-amber-600 mt-2">Falta la variable PUBLIC_BASE_URL (la dirección pública del sistema) para registrar los avisos.</p>
              )}
              <Button variant="outline" disabled={!esAdmin || !conectado || !estado.url_avisos || registrando} onClick={registrarAvisos} className="rounded-full gap-2 mt-3" data-testid="shopify-registrar-avisos">
                <Bell className="h-4 w-4" /> {registrando ? "Registrando…" : "Registrar avisos en la tienda"}
              </Button>
              {avisosRes && (
                <ul className="mt-3 space-y-1" data-testid="shopify-avisos-resultado">
                  {avisosRes.map((a) => (
                    <li key={a.topic} className={`text-xs ${a.estado === "error" ? "text-red-500" : "text-muted-foreground"}`}>
                      {AVISO_TOPICS[a.topic] || a.topic}: {AVISO_REGISTRO[a.estado] || a.estado}{a.detalle ? ` (${a.detalle})` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </Paso>
          </div>
        </TabsContent>

        <TabsContent value="matching">
          {!conectado ? (
            <EmptyState icon={Link2} title="Shopify pendiente de conexión" subtitle="Cuando carguemos las credenciales, acá vas a poder vincular los productos del sistema con los que ya existen en tu tienda." />
          ) : (
            <div className="space-y-3">
              <div className="ms-card p-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground flex-1 min-w-[220px]">
                  {estado.productos.sin_vincular} productos del sistema no están vinculados con la web. El sistema no crea productos en Shopify: acá solo se vinculan los que ya existen en las dos partes.
                </p>
                <Button disabled={buscando} onClick={buscarCoincidencias} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="shopify-buscar-coincidencias">
                  <RefreshCw className={`h-4 w-4 ${buscando ? "animate-spin" : ""}`} /> {buscando ? "Leyendo la web…" : "Buscar coincidencias"}
                </Button>
              </div>
              {matching === null ? (
                <p className="text-sm text-muted-foreground py-2">Tocá «Buscar coincidencias» para leer la web y ver qué producto de la tienda se parece a cada uno de los tuyos.</p>
              ) : matching.length === 0 ? (
                <EmptyState icon={CheckCircle2} title="Sin coincidencias pendientes" subtitle="No encontramos productos sin vincular que se parezcan a alguno de la web." />
              ) : (
                <div className="space-y-2">
                  {matching.map((m) => (
                    <div key={m.product_id} className="ms-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`match-${m.product_id}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm">{m.nombre} {m.sku && <span className="text-xs text-muted-foreground">· {m.sku}</span>}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Sugerido: {m.shopify_title} · coincidencia {Math.round(m.score * 100)}%</p>
                      </div>
                      {esAdmin || user?.role === "contadora" ? (
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => resolver(m, "vincular")} data-testid={`match-vincular-${m.product_id}`}><Link2 className="h-3.5 w-3.5" /> Vincular</Button>
                          <Button size="sm" variant="ghost" className="rounded-full gap-1 text-muted-foreground" onClick={() => resolver(m, "ignorar")} data-testid={`match-ignorar-${m.product_id}`}><EyeOff className="h-3.5 w-3.5" /> Ignorar</Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="log">
          <div className="space-y-4">
            <div>
              <h3 className="font-head font-semibold text-sm mb-2">Operaciones de catálogo</h3>
              {log.length === 0 ? <p className="text-sm text-muted-foreground py-4">Sin operaciones todavía.</p> : (
                <div className="space-y-2">
                  {log.map((l) => (
                    <div key={l.id} className="ms-card p-3 flex items-center justify-between gap-3" data-testid={`shopify-log-${l.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm"><span className="font-semibold">{LOG_ACCIONES[l.accion] || l.accion}</span>{l.producto_nombre ? ` · ${l.producto_nombre}` : ""} · <span className={l.resultado === "ok" ? "text-emerald-600" : "text-red-500"}>{l.resultado}</span></p>
                        {l.detalle && <p className="text-xs text-muted-foreground truncate">{l.detalle}</p>}
                      </div>
                      <span className="text-[11px] text-muted-foreground shrink-0">{dt(l.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3 className="font-head font-semibold text-sm mb-2 flex items-center gap-1"><ShoppingCart className="h-4 w-4" /> Pedidos entrantes (webhooks)</h3>
              {webhookLogs.length === 0 ? <p className="text-sm text-muted-foreground py-4">Sin pedidos recibidos todavía.</p> : (
                <div className="space-y-2">
                  {webhookLogs.map((w) => (
                    <div key={w.id} className="ms-card p-3 flex items-center justify-between gap-3" data-testid={`shopify-webhook-${w.id}`}>
                      <p className="text-sm">Pedido <span className={w.resultado === "ok" ? "text-emerald-600" : "text-red-500"}>{w.resultado}</span> · {w.tiempo_procesamiento_ms} ms</p>
                      <span className="text-[11px] text-muted-foreground shrink-0">{dt(w.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirmar} onOpenChange={(o) => !o && setConfirmar(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{confirmar?.titulo}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{confirmar?.texto}</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="shopify-confirmar-ok"
              onClick={async () => { const hacer = confirmar.accion; setConfirmar(null); await hacer(); }}>
              {confirmar?.boton || "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
